import React, { useRef, useState } from 'react';
import { useFecharAoClicarFora } from '../utils/fecharAoClicarFora';
import './SeletorDeMes.css';

/**
 * Escolher um MÊS, em português.
 *
 * ── Por que não `<input type="month">` ────────────────────────────────────
 *
 * Era o que estava lá, e o relato foi direto: "melhorar esse input do prazo, ta
 * ruim". Três problemas, e o primeiro sozinho já bastava:
 *
 * 1. **Ele fala a língua do sistema, não a do app.** Aparecia "January 2027"
 *    numa tela inteiramente em português. O controle nativo usa o locale do
 *    navegador e não há como pedir outro — nem `lang` no elemento resolve.
 *
 * 2. **Ele não se parece com nada aqui.** Fonte, altura, o ícone de
 *    calendário e o comportamento de foco vêm do sistema. No meio de um
 *    formulário com campos e chips do app, é a única peça que denuncia que
 *    não foi desenhada.
 *
 * 3. **No celular ele abre a roleta nativa**, que pede rolagem em duas
 *    colunas para escolher entre doze coisas — quando doze cabem numa grade
 *    que se vê inteira.
 *
 * ── Por que grade e não dois `<select>` ──────────────────────────────────
 *
 * Dois selects seriam menos código e resolveriam o idioma. Mas escolher um
 * prazo é comparar: "dezembro é perto demais? e março?". Numa grade os doze
 * meses estão à vista e a comparação é um olhar; em dois selects fechados ela
 * é abrir, rolar, fechar, reabrir.
 *
 * ── O ano tem setas, e é por isso que o padrão é o ano corrente ──────────
 *
 * Quase todo prazo cai nos próximos doze meses. Abrir no ano de hoje faz o
 * caso comum ser um clique, e o distante custa uma seta por ano — que é a
 * proporção certa entre os dois.
 */

const MESES = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

const MESES_LONGOS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/** 'YYYY-MM' -> "dez/2026". Exportado: a tela de metas rotula com o mesmo. */
export const rotuloDoMes = (mk: string): string => {
  const [y, m] = mk.split('-').map(Number);
  return `${MESES[m - 1]}/${y}`;
};

export interface SeletorDeMesProps {
  /** 'YYYY-MM', ou '' para "sem prazo". */
  value: string;
  onChange: (valor: string) => void;
  /** O que dizer quando não há mês escolhido. */
  vazio?: string;
  /** Não deixa escolher antes deste mês. Prazo no passado não é prazo. */
  minimo?: string;
  id?: string;
}

const SeletorDeMes: React.FC<SeletorDeMesProps> = ({
  value, onChange, vazio = 'Sem prazo', minimo, id,
}) => {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  /*
   * O ano visível é ESTADO, não derivado do valor.
   *
   * Derivado, navegar para 2028 e não escolher nada saltaria de volta ao ano
   * do valor no próximo render — a navegação se desfaria sozinha enquanto ela
   * ainda estava decidindo.
   */
  const anoDoValor = value ? Number(value.slice(0, 4)) : new Date().getFullYear();
  const [ano, setAno] = useState(anoDoValor);

  useFecharAoClicarFora(caixa, aberto, () => setAberto(false));

  const escolher = (mes: number) => {
    onChange(`${ano}-${String(mes + 1).padStart(2, '0')}`);
    setAberto(false);
  };

  const abrir = () => {
    // Reabrir mostra o ano do valor ATUAL, não o último que ela navegou e
    // abandonou. Sair sem escolher desfaz a navegação, que é o que "sair sem
    // escolher" quer dizer.
    setAno(anoDoValor);
    setAberto(true);
  };

  const chaveDe = (mes: number) => `${ano}-${String(mes + 1).padStart(2, '0')}`;
  const bloqueado = (mes: number) => !!minimo && chaveDe(mes) < minimo;

  return (
    <div className="sm" ref={caixa}>
      <button
        type="button"
        id={id}
        className={`sm-gatilho${value ? ' tem-valor' : ''}`}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-haspopup="dialog"
        aria-expanded={aberto}
      >
        <span>{value ? rotuloDoMes(value) : vazio}</span>
        {/* Limpar vive DENTRO do gatilho como um `<span>`, não como um botão
            aninhado: botão dentro de botão é HTML inválido e o clique interno
            fica indefinido entre navegadores. */}
        {value ? (
          <span
            className="sm-limpar"
            role="button"
            tabIndex={0}
            aria-label="Tirar o prazo"
            onClick={e => { e.stopPropagation(); onChange(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault(); e.stopPropagation(); onChange('');
              }
            }}
          >
            ✕
          </span>
        ) : (
          <span className="sm-seta" aria-hidden="true">▾</span>
        )}
      </button>

      {aberto && (
        <div className="sm-painel" role="dialog" aria-label="Escolher mês">
          <div className="sm-ano">
            <button type="button" className="sm-ano-seta" onClick={() => setAno(a => a - 1)}
                    aria-label="Ano anterior">‹</button>
            <span className="sm-ano-rotulo">{ano}</span>
            <button type="button" className="sm-ano-seta" onClick={() => setAno(a => a + 1)}
                    aria-label="Próximo ano">›</button>
          </div>

          <div className="sm-grade">
            {MESES.map((m, i) => (
              <button
                key={m}
                type="button"
                className={`sm-mes${value === chaveDe(i) ? ' escolhido' : ''}`}
                disabled={bloqueado(i)}
                onClick={() => escolher(i)}
                // O nome inteiro no title: a grade é abreviada para caber, e
                // "mai" e "mar" se confundem de relance.
                title={`${MESES_LONGOS[i]} de ${ano}`}
              >
                {m}
              </button>
            ))}
          </div>

          {value && (
            <button type="button" className="sm-sem-prazo" onClick={() => { onChange(''); setAberto(false); }}>
              {vazio}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SeletorDeMes;
